import { ChevronDown, ChevronUp, XClose } from '@/assets/icons'
import clsx from 'clsx'
import { BottomSheetHeight } from '@/components/bottom-sheet/constants'
import { IconButton } from '@/components/icon-button/IconButton'

const bottomSheetHeightMap: Record<BottomSheetHeight, string> = {
  [BottomSheetHeight.SM]: 'max-h-[300px]',
  [BottomSheetHeight.MD]: 'max-h-[400px]',
  [BottomSheetHeight.LG]: 'max-h-[500px]',
  [BottomSheetHeight.XL]: 'max-h-[600px]',
  [BottomSheetHeight.FULL]: 'max-h-[85vh]',
} as const

// Keep the height constant when the sheet is minimized
const minimizedHeight = 'h-12'

type SheetExpandedHeight = keyof typeof bottomSheetHeightMap

type BottomSheetProps = {
  /**
   * The content to render inside the sheet.
   */
  children?: React.ReactNode
  /**
   * When expanded, rendered below the title and above the scrollable `children`; does not scroll with the body.
   */
  fixedContent?: React.ReactNode
  /**
   * The title to display in the header.
   */
  title: React.ReactNode
  /**
   * Whether the sheet is expanded.
   */
  expanded: boolean
  /**
   * When provided, enables expanding/minimizing via the header and shows the expand toggle icon.
   */
  onExpandedChange?: (expanded: boolean) => void
  /**
   * When provided, shows the close icon and enables closing.
   */
  onClose?: () => void
  /**
   * The height of the sheet when expanded.
   */
  expandedHeight?: SheetExpandedHeight
  /**
   * Accessible label for the expand/minimize control when the sheet is minimized.
   * @default "Expand"
   */
  expandAriaLabel?: string
  /**
   * Accessible label for the expand/minimize control when the sheet is expanded.
   * @default "Collapse"
   */
  collapseAriaLabel?: string
  /**
   * When true, applies a left offset on desktop to account for the sidebar width.
   * Set to false when the sidebar is hidden so the sheet spans the full viewport width.
   * @default true
   */
  offsetForSidebar?: boolean
}

export function BottomSheet({
  children = null,
  fixedContent = null,
  expanded,
  onExpandedChange,
  onClose,
  title,
  expandedHeight = BottomSheetHeight.LG,
  expandAriaLabel = 'Expand',
  collapseAriaLabel = 'Collapse',
  offsetForSidebar = true,
}: BottomSheetProps) {
  const isEmptyState = children == null
  const showActions = !isEmptyState && (onExpandedChange != null || onClose != null)

  const handleHeaderClick = () => {
    if (onExpandedChange) {
      onExpandedChange(!expanded)
    }
  }

  return (
    <div
      className={clsx(
        'fixed bottom-0 right-0 z-[999]',
        'left-0', // Full width on mobile, offset by sidebar on desktop
        offsetForSidebar && 'lg:left-64 3xl:left-80', // matches DashboardLayout sidebar w-64 / 3xl:w-80
        'flex justify-center px-5',
        'transition-[left] ease-in-out duration-500'
      )}
    >
      {/*
        Intentionally tagging the inner sheet, not the outer wrapper. The outer is a full-width
        positioning shell whose area includes empty padding around the visible sheet — tagging it
        would misreport hits on that padding as "BottomSheet".
      */}
      <div
        data-ds-component="BottomSheet"
        data-ds-size={expandedHeight}
        className={clsx(
          'w-full',
          isEmptyState ? 'bg-brand-50 border-x border-t border-brand-200' : 'bg-brand-neutral-50 border border-brand-neutral-200',
          'rounded-t-2xl',
          expanded
            ? clsx(
                bottomSheetHeightMap[expandedHeight],
                // Column layout whenever expanded; overflow-hidden only once there is a body (scroll region).
                'flex flex-col min-h-0',
                !isEmptyState && 'overflow-hidden'
              )
            : minimizedHeight
        )}
      >
        {/* Header */}
        <div className={clsx('flex items-center justify-between gap-1 z-10', isEmptyState ? 'px-4 py-3 justify-center' : 'pt-1 mx-1')}>
          <button
            type="button"
            onClick={handleHeaderClick}
            className={clsx(
              'w-full font-semibold font-rethink-sans text-sm xs:text-base truncate',
              isEmptyState ? 'flex items-center justify-center text-center ml-0' : 'ml-4 text-left',
              'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-neutral-500'
            )}
          >
            {title}
          </button>

          {showActions && (
            <div className="pt-1 mx-1 flex items-center gap-1">
              {onExpandedChange && (
                <IconButton
                  aria-label={expanded ? collapseAriaLabel : expandAriaLabel}
                  color="neutral"
                  icon={expanded ? <ChevronDown /> : <ChevronUp />}
                  onClick={handleHeaderClick}
                  size="md"
                  variant="plain"
                />
              )}
              {onClose && <IconButton variant="plain" color="neutral" size="md" aria-label="Close uploads" icon={<XClose />} onClick={() => onClose?.()} />}
            </div>
          )}
        </div>

        {/* Body: optional fixed strip, then scroll. max-h stays on outer shell only. */}
        {expanded && !isEmptyState ? (
          <div className={clsx('flex min-h-0 flex-1 flex-col', fixedContent != null && 'gap-2')}>
            {fixedContent != null ? <div className="shrink-0">{fixedContent}</div> : null}
            <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
